import React from "react";
import { format } from "date-fns-tz";
import Typography from "@mui/material/Typography";
import { daysArrayToString } from "../../modules/RecurringBookingsUtilityFunctions";

const ProviderScheduleDescription = ({
    item,
    schedInfo,
    schedBlock,
    action
}) => {
    if (!item.data.schedChange) return null;

    const { userName, userRole, data } = item;
    const { provider, location, newInfo, schedOld } = data;

    switch (action) {
        case "CREATE":
            return (
                <>
                    {schedBlock === "CREATE_SCHEDULE" && (
                        <CreateScheduleMessage
                            userName={userName}
                            userRole={userRole}
                            provider={provider}
                        />
                    )}
                    {schedBlock === "CREATE_BLOCKED_TIME" && (
                        <CreateBlockedTimeMessage
                            userName={userName}
                            userRole={userRole}
                            provider={provider}
                        />
                    )}
                    {schedBlock === "CREATE_TRAVEL_ZONE" && (
                        <CreateTravelZoneMessage
                            userName={userName}
                            userRole={userRole}
                            provider={provider}
                        />
                    )}
                    {schedBlock === "ADD_TB" && (
                        <AddAvailabilityMessage
                            updates={newInfo[0]}
                            userName={userName}
                            schedInfo={schedInfo}
                        />
                    )}
                    {schedBlock === "ADD_BLOCKTIME" && (
                        <AddBlockedTimeMessage
                            userName={userName}
                            schedInfo={schedInfo}
                            provider={schedInfo.provider}
                        />
                    )}
                    {schedBlock === true && (
                        <GeneralCreateMessage
                            userName={userName}
                            updates={newInfo[0]}
                            schedInfo={schedInfo}
                        />
                    )}
                </>
            );
        case "UPDATE":
            return (
                <>
                    {schedBlock === "UPDATE_SCHEDULE" && (
                        <UpdateScheduleMessage
                            userName={userName}
                            provider={provider}
                            userRole={userRole}
                            schedOld={schedOld}
                            newInfo={newInfo[0]}
                        />
                    )}
                    {schedBlock === "UPDATE_BLOCKED_TIME" && (
                        <UpdateBlockedTimeMessage
                            userName={userName}
                            provider={provider}
                            userRole={userRole}
                            schedOld={schedOld}
                            newInfo={newInfo[0]}
                        />
                    )}
                    {schedBlock === "UPDATE_TRAVEL_ZONE" && (
                        <UpdateTravelZoneMessage
                            userName={userName}
                            userRole={userRole}
                            provider={provider}
                        />
                    )}
                    {schedBlock === "EDIT_TB" && (
                        <EditAvailabilityMessage
                            updates={item.data.newInfo[0]}
                            userName={userName}
                            schedInfo={schedInfo}
                        />
                    )}
                    {schedBlock === "UPDATE_BLOCKTIME" && (
                        <UpdateBlockedTimeInfo
                            userName={item.userName}
                            provider={schedInfo.provider}
                            startDate={schedInfo.startDate}
                            endDate={schedInfo.endDate}
                            startTime={schedInfo.startTime}
                            endTime={schedInfo.endTime}
                            weekDays={schedInfo.weekDays}
                        />
                    )}
                    {schedBlock === "ACTIVATE" && (
                        <ActivateScheduleMessage
                            userName={userName}
                            provider={provider}
                            location={location}
                        />
                    )}
                    {schedBlock === "EDIT_SCHED" && (
                        <EditScheduleMessage
                            userName={userName}
                            provider={provider}
                            location={location}
                            services={schedInfo.services}
                        />
                    )}
                </>
            );
        case "DELETE":
            return (
                <>
                    {schedBlock === "DELETE_SCHEDULE" && (
                        <DeleteScheduleMessage
                            userName={userName}
                            provider={provider}
                            location={location}
                        />
                    )}
                    {schedBlock === "DELETE_BLOCKED_TIME" && (
                        <DeleteBlockedTimeMessage
                            userName={userName}
                            provider={provider}
                        />
                    )}
                    {schedBlock === "DEL_BLOCKTIME" && (
                        <DeleteBlockedTimeInfo
                            userName={userName}
                            provider={provider}
                            startDate={schedInfo.startDate}
                            endDate={schedInfo.endDate}
                            startTime={schedInfo.startTime}
                            endTime={schedInfo.endTime}
                            weekDays={schedInfo.weekDays}
                        />
                    )}
                    {schedBlock === true && (
                        <GeneralDeleteMessage
                            userName={userName}
                            itemType={data.itemType}
                            itemName={data.itemName}
                        />
                    )}
                </>
            );
        default:
            return null;
    }
};

const CreateScheduleMessage = ({ userName, userRole, provider }) => {
    if (userRole === "PROVIDER") {
        return (
            <Typography variant="body2">{`${userName} has created a schedule`}</Typography>
        );
    }
    return (
        <Typography variant="body2">{`${userName} has created a schedule for ${provider.firstname} ${provider.lastname}`}</Typography>
    );
};

const UpdateScheduleMessage = ({
    userName,
    provider,
    userRole,
    schedOld,
    newInfo
}) => {
    let description = `${userName} has made the following changes to `;

    if (userRole === "PROVIDER") {
        description += `a schedule: `;
    } else {
        description += ` ${provider.firstname} ${provider.lastname}'s schedule: `;
    }

    const changes = [];

    if (newInfo) {
        const oldSched = schedOld || null;

        //check for date range changes
        if (oldSched && newInfo.dateRange) {
            const [startDate, endDate] = newInfo.dateRange.split(" - ");
            changes.push(
                `Date range from ${format(new Date(oldSched.SB[0].startDate), "MMM d, yyyy")} - ${format(new Date(oldSched.SB[0].endDate), "MMM d, yyyy")} 
                to ${format(new Date(startDate), "MMM d, yyyy")} - ${format(new Date(endDate), "MMM d, yyyy")}`
            );
        }

        //check for time range changes
        if (newInfo.timeRange && oldSched) {
            const oldTimeRanges = oldSched.SB.map(
                (item) => `${item.startTime} - ${item.endTime}`
            ).join(", ");

            const newTimeRanges = newInfo.timeRange
                .map((timeRange) => timeRange)
                .join(", ");

            changes.push(
                `Time range from ${oldTimeRanges} to ${newTimeRanges}`
            );
        }

        //check for changes in week days
        if (newInfo.weekDays) {
            changes.push(
                `Every ${daysArrayToString(newInfo.weekDays.split("").map(Number))}`
            );
        }

        //check for coverage area type changes
        if (newInfo.coverageAreaType) {
            changes.push(`Coverage area type`);
        }

        //check for changes in locations
        if (newInfo.locations) {
            if (newInfo.locations.ADD?.length) {
                changes.push(`Added locations`);
            }
            if (newInfo.locations.DELETE?.length) {
                changes.push(`Removed locations`);
            }
        }

        //check for changes in services
        if (newInfo.services) {
            if (newInfo.services.ADD?.length) {
                changes.push(`Added services`);
            }
            if (newInfo.services.DELETE?.length) {
                changes.push(`Removed services`);
            }
        }
    }

    if (changes.length > 0) {
        description += changes.join(", ");
    }

    return <Typography variant="body2">{description}</Typography>;
};

const CreateBlockedTimeMessage = ({ userName, userRole, provider }) => {
    if (userRole === "PROVIDER") {
        return (
            <Typography variant="body2">
                {userName} has created a blocked time
            </Typography>
        );
    }
    return (
        <Typography variant="body2">
            {userName} has created a blocked time for {provider?.firstname}{" "}
            {provider?.lastname}
        </Typography>
    );
};

const CreateTravelZoneMessage = ({ userName, userRole, provider }) => {
    if (userRole === "PROVIDER") {
        return (
            <Typography variant="body2">{`${userName} has created a travel zone for a schedule`}</Typography>
        );
    }
    return (
        <Typography variant="body2">{`${userName} has created a travel zone for ${provider.firstname} ${provider.lastname}'s schedule`}</Typography>
    );
};

const UpdateTravelZoneMessage = ({ userName, userRole, provider }) => {
    if (userRole === "PROVIDER") {
        return (
            <Typography variant="body2">{`${userName} has made a travel zone change to a schedule`}</Typography>
        );
    }
    return (
        <Typography variant="body2">{`${userName} has made a travel zone change to ${provider.firstname} ${provider.lastname}'s schedule`}</Typography>
    );
};

const UpdateBlockedTimeMessage = ({
    userName,
    provider,
    userRole,
    schedOld,
    newInfo
}) => {
    let description = `${userName} has made the following changes to `;

    if (userRole === "PROVIDER") {
        description += `a blocked time: `;
    } else {
        description += ` ${provider.firstname} ${provider.lastname}'s blocked time: `;
    }

    const changes = [];

    if (newInfo) {
        const oldSched = schedOld || null;

        //check for date range changes
        if (oldSched && newInfo.dateRange) {
            const [startDate, endDate] = newInfo.dateRange.split(" - ");
            changes.push(
                `Date range from ${format(new Date(oldSched.BUT[0].startDate), "MMM d, yyyy")} - ${format(new Date(oldSched.BUT[0].endDate), "MMM d, yyyy")} 
                to ${format(new Date(startDate), "MMM d, yyyy")} - ${format(new Date(endDate), "MMM d, yyyy")}`
            );
        }

        //check for time range changes
        if (newInfo.timeRange && oldSched) {
            const oldTimeRanges = oldSched.BUT.map(
                (item) => `${item.startTime} - ${item.endTime}`
            ).join(", ");

            const newTimeRanges = newInfo.timeRange
                .map((timeRange) => timeRange)
                .join(", ");

            changes.push(
                `Time range from ${oldTimeRanges} to ${newTimeRanges}`
            );
        }

        //check for changes in week days
        if (newInfo.weekDays) {
            changes.push(
                `Every ${daysArrayToString(newInfo.weekDays.split("").map(Number))}`
            );
        }

        //check for changes in week days
        if (newInfo.notes) {
            changes.push(`Notes: "${newInfo.notes}"`);
        }
    }

    if (changes.length > 0) {
        description += changes.join(", ");
    }

    return <Typography variant="body2">{description}</Typography>;
};

const DeleteBlockedTimeMessage = ({ userName, provider }) => (
    <Typography variant="body2">
        {userName} has deleted a blocked time for {provider.firstname}{" "}
        {provider.lastname}
    </Typography>
);

const AddAvailabilityMessage = ({ updates, userName, schedInfo }) => {
    let locInfo =
        updates.locname && updates.locname.length > 0
            ? updates.locname[0]
            : "no locname";
    let locName = locInfo.phone ? locInfo.locationname : locInfo.name;

    const formattedDate = format(new Date(updates.startDate), "yyyy-MM-dd");
    const formattedStartTime = format(new Date(updates.startTime), "hh:mm a");
    const formattedEndTime = format(new Date(updates.endTime), "hh:mm a");

    return (
        <Typography variant="body2">
            {userName} has added an availability for{" "}
            {schedInfo?.provider?.firstname} {schedInfo?.provider?.lastname}{" "}
            with the location: {locName}. Start Date: {formattedDate}, Time
            Range: {formattedStartTime} - {formattedEndTime}. Please see the
            availability for more information.
        </Typography>
    );
};

const AddBlockedTimeMessage = ({ userName, schedInfo, provider }) => {
    return (
        <Typography variant="body2">
            {userName} has added a blocked time for {provider.firstname}{" "}
            {provider.lastname}. Blocked Time Info {"\u2192"} Date Range:{" "}
            {schedInfo.startDate} to {schedInfo.endDate}, Time Range:{" "}
            {schedInfo.startTime} - {schedInfo.endTime}, Repeating?{" "}
            {schedInfo.weekDays === ""
                ? "No"
                : "Yes. Please see blocked time for more info"}
        </Typography>
    );
};

const EditAvailabilityMessage = ({ updates, userName, schedInfo }) => {
    let locInfo =
        updates.locname && updates.locname.length > 0
            ? updates.locname[0]
            : "no locname";
    let locName = locInfo.phone ? locInfo.locationname : locInfo.name;

    const formattedDate = format(new Date(updates.startDate), "yyyy-MM-dd");
    const formattedStartTime = format(new Date(updates.startTime), "hh:mm a");
    const formattedEndTime = format(new Date(updates.endTime), "hh:mm a");

    return (
        <Typography variant="body2">
            {userName} has edited an availability for{" "}
            {schedInfo.provider.firstname} {schedInfo.provider.lastname}
            with the location: {locName}. New Information {"\u2192"} Start Date:{" "}
            {formattedDate}, Time Range: {formattedStartTime} -{" "}
            {formattedEndTime}. Please see the availability for more information
        </Typography>
    );
};

const UpdateBlockedTimeInfo = ({
    userName,
    provider,
    startDate,
    endDate,
    startTime,
    endTime,
    weekDays
}) => (
    <Typography variant="body2">
        {userName} has updated blocked time for {provider.firstname}{" "}
        {provider.lastname}. Details {"\u2192"} Date Range: {startDate} to{" "}
        {endDate}, Time Range: {startTime} - {endTime}, Repeating?{" "}
        {weekDays === ""
            ? "No"
            : "Yes. Please see blocked time for more details."}
    </Typography>
);

const GeneralCreateMessage = ({ userName, updates, schedInfo }) => {
    let locInfo = updates || "No Location name";
    let locName = locInfo.phone ? locInfo.locationname : locInfo.name;
    return (
        <Typography variant="body2">
            {userName} has created a schedule for:{" "}
            {schedInfo?.provider?.firstname} {schedInfo?.provider?.lastname}{" "}
            with the location: {locName}. Please see the provider's schedule
            page for more information
        </Typography>
    );
};

const DeleteScheduleMessage = ({ userName, provider }) => (
    <Typography variant="body2">
        {userName} has deleted a schedule for: {provider.firstname}{" "}
        {provider.lastname}.
    </Typography>
);

const DeleteBlockedTimeInfo = ({
    userName,
    provider,
    startDate,
    endDate,
    startTime,
    endTime,
    weekDays
}) => (
    <Typography variant="body2">
        {userName} has deleted blocked time for {provider.firstname}{" "}
        {provider.lastname}. Details {"\u2192"} Date Range: {startDate} to{" "}
        {endDate}, Time Range: {startTime} - {endTime}, Repeating?{" "}
        {weekDays === ""
            ? "No"
            : "Yes. Please see blocked time for more details."}
    </Typography>
);

const GeneralDeleteMessage = ({ userName, itemType, itemName }) => (
    <Typography variant="body2">
        {userName} has deleted {itemType}: {itemName}. Please check for details.
    </Typography>
);

const ActivateScheduleMessage = ({ userName, provider, location }) => (
    <Typography variant="body2">
        {userName} has activated a schedule for: {provider.firstname}{" "}
        {provider.lastname} with the location: {location}. Please see the
        provider's schedule page for more information.
    </Typography>
);

const EditScheduleMessage = ({ userName, provider, location, services }) => (
    <Typography variant="body2">
        {userName} has edited a schedule for {provider.firstname}{" "}
        {provider.lastname} with the location: {location}. Updates made:{" "}
        {services}
    </Typography>
);

export default ProviderScheduleDescription;
